import ServicePage from '@/components/ServicePage';

export default function BugFixingPage() {
  return (
    <ServicePage
      slug="bug-fixing"
      title="Bug Fixing & Debugging Services"
      titleAr="خدمات إصلاح الأخطاء والتصحيح"
      description="Professional software debugging and bug fixing services in Egypt. We identify root causes, resolve critical issues, and prevent regressions using advanced tools and AI-assisted analysis."
      descriptionAr="خدمات تصحيح البرمجيات وإصلاح الأخطاء الاحترافية في مصر. نحدد الأسباب الجذرية، ونحل المشكلات الحرجة، ونمنع الانحدارات باستخدام أدوات متقدمة وتحليل مدعوم بالذكاء الاصطناعي."
      keywords="bug fixing services, software debugging Egypt, application debugging, error resolution, code debugging, bug hunting, software quality assurance"
      heroImage="https://readdy.ai/api/search-image?query=Professional%20software%20debugging%20workspace%2C%20developer%20analyzing%20code%20errors%20on%20dark%20themed%20IDE%20with%20multiple%20monitors%2C%20close%20up%20of%20debugging%20process%2C%20modern%20tech%20office%20with%20blue%20ambient%20lighting%2C%20clean%20professional%20photography%20style%2C%20problem%20solving%20concept&width=1400&height=600&seq=svc1&orientation=landscape"
      icon="ri-bug-line"
      features={[
        {
          title: 'Root Cause Analysis',
          titleAr: 'تحليل السبب الجذري',
          description: 'We do not just patch symptoms. Our engineers trace bugs to their origin using static analysis, runtime profiling, and log forensics to deliver permanent fixes.',
          descriptionAr: 'لا نقوم فقط بتصحيح الأعراض. يتتبع مهندسونا الأخطاء إلى أصلها باستخدام التحليل الثابت، وملفات تعريف وقت التشغيل، والتحقيق في السجلات لتقديم إصلاحات دائمة.',
        },
        {
          title: 'Memory Leak Resolution',
          titleAr: 'حل تسربات الذاكرة',
          description: 'We specialize in identifying and fixing memory leaks in Python, Node.js, Java, and C# applications that cause crashes and slowdowns over time.',
          descriptionAr: 'نتخصص في تحديد وإصلاح تسربات الذاكرة في تطبيقات Python و Node.js و Java و C# التي تسبب الأعطال والبطء مع مرور الوقت.',
        },
        {
          title: 'Race Condition Fixes',
          titleAr: 'إصلاحات حالات السباق',
          description: 'Multi-threaded applications are prone to race conditions. We analyze concurrency patterns, implement proper locking, and fix synchronization issues.',
          descriptionAr: 'تطبيقات متعددة الخيوط عرضة لحالات السباق. نحلل أنماط التزامن، وننفذ قفلاً مناسباً، ونصلح مشاكل التزامن.',
        },
        {
          title: 'Database Connection Issues',
          titleAr: 'مشاكل اتصال قاعدة البيانات',
          description: 'We resolve connection pool exhaustion, query timeouts, deadlock scenarios, and ORM misconfigurations that cause intermittent failures.',
          descriptionAr: 'نحل استنفاد تجمع الاتصالات، وانتهاء مهام الاستعلام، وسيناريوهات الاستنزاف، وسوء تكوين ORM التي تسبب أعطالاً متقطعة.',
        },
        {
          title: 'Frontend Error Handling',
          titleAr: 'معالجة أخطاء الواجهة الأمامية',
          description: 'JavaScript errors, React component crashes, and API integration failures in the frontend are diagnosed and fixed with comprehensive testing.',
          descriptionAr: 'يتم تشخيص وإصلاح أخطاء JavaScript، وأعطال مكونات React، وفشل تكامل API في الواجهة الأمامية مع اختبار شامل.',
        },
        {
          title: 'Regression Testing',
          titleAr: 'اختبار الانحدار',
          description: 'Every fix is verified with automated regression tests to ensure the bug stays resolved and no new issues are introduced.',
          descriptionAr: 'يتم التحقق من كل إصلاح باستخدام اختبارات الانحدار الآلية لضمان بقاء الخطأ محلولاً وعدم إدخال مشاكل جديدة.',
        },
      ]}
      benefits={[
        {
          title: '24-48 Hour Turnaround',
          titleAr: 'تحول في 24-48 ساعة',
          description: 'Most critical bugs are identified and fixed within 24-48 hours. Urgent issues get priority treatment with immediate response.',
          descriptionAr: 'يتم تحديد وإصلاح معظم الأخطاء الحرجة في غضون 24-48 ساعة. تحصل المشاكل العاجلة على معاملة ذات أولوية مع استجابة فورية.',
        },
        {
          title: 'Permanent Fixes',
          titleAr: 'إصلاحات دائمة',
          description: 'We solve the root cause, not just the symptom. Our fixes prevent the same bug from recurring and include documentation for your team.',
          descriptionAr: 'نحل السبب الجذري، وليس العرض فقط. تمنع إصلاحاتنا عودة نفس الخطأ وتتضمن توثيقاً لفريقك.',
        },
        {
          title: 'All Technologies Covered',
          titleAr: 'جميع التقنيات المغطاة',
          description: 'From React and Vue to Python Django and Node.js, we debug across the full modern stack including mobile apps in Flutter and React Native.',
          descriptionAr: 'من React و Vue إلى Python Django و Node.js، نصحح الأخطاء عبر المكدس الحديث بأكمله بما في ذلك تطبيقات الهاتف المحمول في Flutter و React Native.',
        },
        {
          title: 'Confidential & Secure',
          titleAr: 'سرية وآمنة',
          description: 'All code access is protected under NDA. Your intellectual property remains yours, and we follow strict security protocols throughout.',
          descriptionAr: 'جميع الوصول إلى الكود محمي بموجب اتفاقية عدم الإفشاء. تظل ملكيتك الفكرية ملكاً لك، ونتبع بروتوكولات أمان صارمة على طول الطريق.',
        },
      ]}
      faqs={[
        {
          question: 'How quickly can you fix a bug in my application?',
          questionAr: 'ما مدى سرعة إصلاح خطأ في تطبيقي؟',
          answer: 'For critical production bugs, we can start diagnosis within hours. Most issues are resolved within 24-48 hours. Complex architectural bugs may take 3-5 days, but we provide daily progress updates.',
          answerAr: 'بالنسبة للأخطاء الحرجة في الإنتاج، يمكننا بدء التشخيص في غضون ساعات. يتم حل معظم المشاكل في غضون 24-48 ساعة. قد تستغرق الأخطاء المعمارية المعقدة 3-5 أيام، لكننا نقدم تحديثات يومية.',
        },
        {
          question: 'What types of bugs do you specialize in fixing?',
          questionAr: 'ما هي أنواع الأخطاء التي تتخصص في إصلاحها؟',
          answer: 'We fix memory leaks, race conditions, database deadlocks, API integration failures, frontend crashes, authentication issues, and performance bottlenecks. We also handle security vulnerabilities and logic errors.',
          answerAr: 'نصلح تسربات الذاكرة، وحالات السباق، والاستنزاف في قواعد البيانات، وفشل تكامل API، وأعطال الواجهة الأمامية، ومشاكل المصادقة، والاختناقات في الأداء. نتعامل أيضاً مع الثغرات الأمنية وأخطاء المنطق.',
        },
        {
          question: 'Do you provide ongoing debugging support?',
          questionAr: 'هل تقدمون دعماً مستمراً للتصحيح؟',
          answer: 'Yes, we offer monthly debugging retainer packages. This includes proactive monitoring, periodic code reviews, and priority access to our debugging team whenever issues arise.',
          answerAr: 'نعم، نقدم حزم تصحيح شهرية. يتضمن ذلك المراقبة الاستباقية، ومراجعات الكود الدورية، والوصول ذي الأولوية إلى فريق التصحيح لدينا كلما نشأت مشاكل.',
        },
      ]}
      relatedServices={[
        { slug: 'custom-development', title: 'Custom Development', titleAr: 'التطوير المخصص', icon: 'ri-code-s-slash-line' },
        { slug: 'performance-optimization', title: 'Performance Optimization', titleAr: 'تحسين الأداء', icon: 'ri-speed-line' },
        { slug: 'security-audits', title: 'Security Audits', titleAr: 'تدقيق الأمان', icon: 'ri-shield-check-line' },
      ]}
    />
  );
}